import { ViewportScroller } from '@angular/common'
import { EnvironmentProviders, inject, makeEnvironmentProviders, provideEnvironmentInitializer } from '@angular/core'
import { EventType, NavigationEnd, Router, Scroll } from '@angular/router'
import { filter, map, pairwise } from 'rxjs'

import { filterIfInstanceOf } from 'app/utils/rxjs/filter-if-instance-of.operator'

function cleanPath(path: string): string {
  return path
    .replace(/[?#].+$/, '') // remove QueryParams and or Fragment
    .replaceAll(/;[^/]+/g, '') // remove MatrixParams
}

/**
 * Custom scroll restoration strategy for route navigations.
 * Replaces Angular's default scroll behavior with context-aware handling:
 * back/forward restores position, anchor links scroll to fragment, and
 * forward navigations scroll to top only when the path actually changes.
 */
export function provideRouteScrollHandler(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      const router = inject(Router)
      const viewportScroller = inject(ViewportScroller)

      router.events
        .pipe(
          filterIfInstanceOf(Scroll),

          // only act once the navigation has fully resolved
          filter(
            (event): event is Scroll & { routerEvent: NavigationEnd } =>
              event.routerEvent.type === EventType.NavigationEnd,
          ),

          // pair each scroll event with the cleaned path (no query/matrix params)
          map((event) => [event, cleanPath(event.routerEvent.urlAfterRedirects)] as const),

          // combine consecutive navigations to detect path changes; we never need to look at the first one.
          pairwise(),
        )
        .subscribe(([[, prevPath], [{ position, anchor }, newPath]]) => {
          if (position) {
            // backward navigation – restore saved scroll position
            setTimeout(() => viewportScroller.scrollToPosition(position))
          } else if (anchor) {
            // anchor navigation – scroll to the targeted fragment
            setTimeout(() => viewportScroller.scrollToAnchor(anchor))
          } else {
            // forward navigation – scroll to top only if the path changed
            // (keeps position when only query/matrix params change)
            if (prevPath !== newPath) {
              setTimeout(() => viewportScroller.scrollToPosition([0, 0]))
            }
          }
        })
    }),
  ])
}
